<template>
  <ValidationObserver
    ref="observer"
    slim
  >
    <content-editor-modal
      class="survey-editor-modal"
      :title="editGroup ? 'Modifier le sondage' : 'Nouveau sondage'"
      @close="closeModal"
    >
      <div class="survey-editor-content">
        <section
          v-for="(survey, index) in surveys"
          :key="index"
          class="survey-question"
        >
          <div class="survey-question-layout">
            <main class="survey-question-main">
              <div class="survey-question-title-row">
                <ValidationProvider
                  name="Question"
                  rules="required|min:10|max:180"
                  v-slot="{ errors }"
                  class="survey-question-title-provider"
                >
                  <input
                    v-model="survey.question"
                    type="text"
                    class="survey-question-input"
                    :class="{ 'survey-question-input--error': errors[0] }"
                    :placeholder="`Question ${index + 1}`"
                    :aria-label="`Question ${index + 1}`"
                  />
                  <p v-if="errors[0]" class="survey-field-error">{{ errors[0] }}</p>
                </ValidationProvider>
                <button
                  v-if="surveys.length > 1"
                  type="button"
                  class="survey-question-remove"
                  :aria-label="`Supprimer la question ${index + 1}`"
                  v-tooltip="'Supprimer cette question'"
                  @click="removeSurvey(index)"
                >
                  <i class="fas fa-times" />
                </button>
              </div>

              <div class="survey-options-list">
                <div
                  v-for="(option, i) in survey.options"
                  :key="i"
                  class="survey-option-row"
                >
                  <span class="survey-option-indicator" aria-hidden="true"></span>
                  <ValidationProvider
                    :name="`Réponse ${i + 1}`"
                    rules="required|min:1|max:180"
                    v-slot="{ errors }"
                    tag="div"
                    class="survey-option-field"
                  >
                    <input
                      v-model="survey.options[i].option"
                      type="text"
                      class="survey-option-input"
                      :class="{
                        'survey-option-input--error': errors[0] || getDuplicateOptionIndexes(survey.options).includes(i),
                        'survey-option-input--locked': isLockedOption(survey.options[i])
                      }"
                      :placeholder="`Option ${i + 1}`"
                      :disabled="isLockedOption(survey.options[i])"
                    />
                    <span
                      v-if="getDuplicateOptionIndexes(survey.options).includes(i)"
                      class="survey-option-error"
                    >Doublon détecté</span>
                  </ValidationProvider>
                  <button
                    v-if="survey.options.length > 1"
                    type="button"
                    class="survey-option-remove"
                    :aria-label="`Supprimer l'option ${i + 1}`"
                    v-tooltip="'Supprimer cette option'"
                    @click="removeOption(index, i)"
                  >
                    <i class="fas fa-times" />
                  </button>
                </div>

                <div class="survey-option-shortcuts">
                  <span class="survey-option-indicator" aria-hidden="true"></span>
                  <span>Ajouter une option </span>
                  <span
                    class="survey-option-link-wrapper"
                    :class="{ 'survey-option-link-wrapper--disabled': hasBlankOption(survey) }"
                    v-tooltip="hasBlankOption(survey) ? 'Remplissez les options précédentes avant d\'en ajouter une.' : 'Ajouter une réponse classique'"
                  >
                    <a
                      href="#"
                      @click.prevent="addOption(index)"
                      :class="{ 'survey-option-link--disabled': hasBlankOption(survey) }"
                    >Classique</a>
                  </span>,
                  <a
                    href="#"
                    @click.prevent="addCustomAnswerOption(index)"
                    :class="{ 'survey-option-link--disabled': survey.allows_custom_answer }"
                    v-tooltip="survey.allows_custom_answer ? 'Réponse personnalisée déjà ajoutée' : 'L\'utilisateur peut saisir une réponse personnalisée'"
                  >Personnalisée</a>,
                  <a
                    href="#"
                    @click.prevent="addNoReplyOption(index)"
                    :class="{ 'survey-option-link--disabled': survey.noReplyAdded }"
                    v-tooltip="survey.noReplyAdded ? 'Option déjà ajoutée' : 'L\'utilisateur peut choisir de ne pas répondre'"
                  >Ne souhaite pas répondre</a>
                </div>
              </div>
            </main>

            <aside class="survey-question-settings">
              <label class="survey-question-setting">
                <span
                  @click="survey.multiple_answers = !survey.multiple_answers"
                  v-tooltip="'Permettre à l\'utilisateur de sélectionner plusieurs réponses à cette question.'"
                >Plusieurs réponses possibles</span>
                <t-toggle
                  v-model="survey.multiple_answers"
                  v-tooltip="'Permettre à l\'utilisateur de sélectionner plusieurs réponses à cette question.'"
                />
              </label>
              <label class="survey-question-setting">
                <span
                  @click="survey.isReanswerable = !survey.isReanswerable"
                  v-tooltip="'Permettre à l\'utilisateur de changer sa réponse après avoir répondu.'"
                >Possibilité de changer de réponse</span>
                <t-toggle
                  v-model="survey.isReanswerable"
                  v-tooltip="'Permettre à l\'utilisateur de changer sa réponse après avoir répondu.'"
                />
              </label>
              <label v-if="surveys.length > 1" class="survey-question-setting">
                <span
                  @click="survey.isRequired = !survey.isRequired"
                  v-tooltip="'L\'utilisateur ne peut pas envoyer le sondage sans avoir répondu à cette question.'"
                >Réponse obligatoire</span>
                <t-toggle
                  v-model="survey.isRequired"
                  v-tooltip="'L\'utilisateur ne peut pas envoyer le sondage sans avoir répondu à cette question.'"
                />
              </label>
            </aside>
          </div>
        </section>
        <section class="survey-general-settings">
          <ValidationProvider
            v-if="!surveyTitleDisabled"
            key="multiple"
            v-slot="{ errors }"
            :rules="groupTitleRules"
            name="Titre du sondage"
          >
            <input
              v-model="grouptitle"
              type="text"
              class="survey-group-title-input"
              :class="{ 'survey-group-title-input--error': errors[0] }"
              placeholder="Titre du sondage"
            />
            <p v-if="errors[0]" class="survey-field-error">{{ errors[0] }}</p>
          </ValidationProvider>

          <div
            v-if="domain.slug === '0-media-interne'"
            class="survey-general-field survey-media-visibility"
          >
            <div class="survey-general-label">Applications cibles</div>
            <div class="survey-media-options">
              <label>
                <input v-model="mediaMvpVisibility" type="radio" name="survey-media-mvp-visibility" value="both" />
                <span>Maviepro &amp; Tract</span>
              </label>
              <label>
                <input v-model="mediaMvpVisibility" type="radio" name="survey-media-mvp-visibility" value="maviepro" />
                <span>Maviepro</span>
              </label>
              <label>
                <input v-model="mediaMvpVisibility" type="radio" name="survey-media-mvp-visibility" value="tract" />
                <span>Tract</span>
              </label>
            </div>
          </div>

          <div class="survey-general-grid">
            <ValidationProvider
              v-if="domain.slug === '0-media-interne'"
              name="Thème"
              :rules="editGroup ? '' : 'required'"
              v-slot="{ errors }"
              class="survey-general-field"
            >
              <div class="survey-general-label">Thème</div>
              <select
                v-model="groupTheme"
                class="survey-theme-select"
                :class="{ 'survey-theme-select--error': errors[0] }"
              >
                <option value="" disabled>Sélectionner...</option>
                <option v-for="theme in themes" :key="theme" :value="theme">{{ theme }}</option>
              </select>
              <p v-if="errors[0]" class="survey-field-error">{{ errors[0] }}</p>
            </ValidationProvider>
            <template v-if="domain.uuid !== '2bc5decc-cd2e-46ad-92d7-f17234fdeebd'">
              <ValidationProvider
                name="Début du sondage"
                rules="required"
                v-slot="{ errors }"
                class="survey-general-field"
              >
                <div class="survey-general-label">Date de début</div>
                <t-datepicker
                  v-model="groupStartAt"
                  timepicker
                  variant="dropup"
                  :maxDate="groupEndAt"
                  placeholder="Sélectionner une date"
                  dateFormat="Y-m-d H:i:S"
                  userFormat="d/m/Y H:i"
                />
                <p v-if="errors[0]" class="survey-field-error">{{ errors[0] }}</p>
              </ValidationProvider>
              <ValidationProvider
                name="Fin du sondage"
                rules="required"
                v-slot="{ errors }"
                class="survey-general-field"
              >
                <div class="survey-general-label">Date de fin</div>
                <t-datepicker
                  v-model="groupEndAt"
                  timepicker
                  variant="dropup"
                  :minDate="groupStartAt"
                  placeholder="Sélectionner une date"
                  dateFormat="Y-m-d H:i:S"
                  userFormat="d/m/Y H:i"
                />
                <p v-if="errors[0]" class="survey-field-error">{{ errors[0] }}</p>
              </ValidationProvider>
            </template>

            <div
              v-if="usersGroups && usersGroups.length"
              class="survey-general-field"
            >
              <div class="survey-general-label">Groupes</div>
              <t-rich-select
                v-model="groupsSelected"
                multiple
                :close-on-select="false"
                :options="usersGroups"
                placeholder="Sélectionner..."
              />
            </div>
          </div>

          <div v-if="errorMessage" class="survey-editor-error">
            {{ errorMessage }}
          </div>

          <div class="survey-editor-actions">
            <div class="survey-editor-actions__left">
              <button
                v-if="!editGroup || !editGroup.is_single"
                type="button"
                class="content-editor-modal__button"
                @click="addSurvey"
              >
                Ajouter une question
              </button>
              <label class="content-editor-modal__status-toggle">
                <t-toggle v-model="groupIsEnabled" />
                <span>{{ groupIsEnabled ? 'Activé' : 'Désactivé' }}</span>
              </label>
              <label
                v-if="domain.can_avoid_notifications"
                class="content-editor-modal__status-toggle"
              >
                <t-toggle
                  v-model="groupToNotify"
                  :value="false"
                  :uncheckedValue="true"
                />
                <span>Ne pas notifier</span>
              </label>
            </div>

            <div class="survey-editor-actions__right">
              <button
                v-if="editGroup && canRenotify"
                type="button"
                class="content-editor-modal__button"
                @click="notify"
              >
                <i class="fa-regular fa-bell"></i> Renotifier
              </button>
              <button
                v-if="editGroup && hasDuplicationRights"
                type="button"
                class="content-editor-modal__button"
                :disabled="duplicatingSurvey"
                @click="duplicateSurvey"
              >
                {{ duplicatingSurvey ? 'Duplication...' : 'Dupliquer' }}
              </button>
              <button
                type="button"
                class="content-editor-modal__button content-editor-modal__button--primary"
                :disabled="hasEmptyQuestion || hasDuplicateOptions || hasEmptyOptions || hasEmptyDates || isTitleMissing"
                v-tooltip="{ content: saveButtonTooltip, html: false }"
                @click.prevent="createGroup"
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </section>
      </div>
    </content-editor-modal>
  </ValidationObserver>
</template>
<script>
import { http } from '@/services/api.service'
import { CUSTOM_ANSWER_OPTION, NO_REPLY_OPTION, isCustomAnswerOption, isLockedOption, isNoReplyOption } from '@/utils/surveyOptions'
import { mapGetters } from 'vuex'
import ContentEditorModal from '@/components/content/ContentEditorModal.vue'

export default {
  name: 'app-survey-group-create',
  components: {
    ContentEditorModal
  },
  props: {
    domainUuid: String,
    canRenotify: {
      type: Boolean,
      default: true
    },
    editGroup: {
      type: Object,
      default: () => null
    }
  },
  computed: {
    ...mapGetters([
      'roles',
      'user',
      'domain'
    ]),
    surveyTitleDisabled () {
      return this.surveys.length <= 1
    },
    groupTitleRules () {
      return this.surveyTitleDisabled ? '' : 'required'
    },
    hasDuplicationRights () {
      return this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    hasDuplicateOptions () {
      return this.surveys.some(survey => this.getDuplicateOptionIndexes(survey.options).length > 0)
    },
    hasEmptyQuestion () {
      return this.surveys.some(survey => !survey.question || survey.question.trim().length < 10)
    },
    hasEmptyOptions () {
      return this.surveys.some(survey => survey.options.some(opt => !opt.option || opt.option.trim() === ''))
    },
    hasEmptyDates () {
      if (this.domain.uuid === '2bc5decc-cd2e-46ad-92d7-f17234fdeebd') {
        return false
      }
      return !this.groupStartAt || !this.groupEndAt
    },
    isTitleMissing () {
      if (this.surveyTitleDisabled) return false
      return !this.grouptitle || this.grouptitle.trim() === ''
    },
    saveButtonTooltip () {
      const errors = []

      if (this.hasEmptyQuestion) {
        errors.push('Veuillez remplir chaque question avec au moins 10 caractères.')
      }
      if (this.hasDuplicateOptions) {
        errors.push('Des doublons sont présents dans les réponses. Veuillez corriger les doublons.')
      }
      if (this.hasEmptyOptions) {
        errors.push('Veuillez remplir toutes les options de réponse.')
      }
      if (this.hasEmptyDates) {
        errors.push('Veuillez sélectionner une date de début et de fin pour le sondage.')
      }
      if (this.isTitleMissing) {
        errors.push('Le titre du sondage est obligatoire lorsqu\'il y a plusieurs questions.')
      }

      if (errors.length === 0) {
        return 'Vous pouvez sauvegarder.'
      }

      return errors.length === 1
        ? errors[0]
        : `Erreurs à corriger :\n• ${errors.join('\n• ')}`
    }
  },
  data () {
    return {
      grouptitle: '',
      surveys: [
        this.createNewSurvey()
      ],
      groupStartAt: null,
      groupEndAt: null,
      groupIsEnabled: false,
      groupToNotify: true,
      groupTheme: '',
      themes: ['Juridique', 'Économie', 'Société', 'Dialogue social'],
      mediaMvpVisibility: 'both',
      usersGroups: [],
      groupsSelected: [],
      errorMessage: '',
      duplicatingSurvey: false
      // isFormValid: false
    }
  },
  async beforeMount () {
    await this.fetchGroups()
  },
  created () {
    if (this.editGroup) this.populateFormForEdit()
  },
  mounted () {
    /* this.$nextTick(() => {
      if (this.$refs.observer) {
        this.$refs.observer.$on('validated', valid => {
          this.isFormValid = valid
        })
      }
    }) */
  },
  methods: {
    isLockedOption,
    closeModal () {
      if (this.$parent && typeof this.$parent.close === 'function') {
        this.$parent.close()
      }
    },
    normalizeMediaMvpVisibility (value) {
      const allowed = ['both', 'maviepro', 'tract']
      return allowed.includes(value) ? value : 'both'
    },
    async fetchGroups () {
      try {
        const response = await http.get('/groups')
        this.usersGroups = response.data.map(group => ({
          value: group.uuid,
          text: group.label
        }))
      } catch (error) {
        console.error('Error fetching groups:', error)
      }
    },
    populateFormForEdit () {
      const group = this.editGroup || {}
      this.grouptitle = group.group_title || ''
      this.groupStartAt = group.start_at || null
      this.groupEndAt = group.end_at || null
      this.groupIsEnabled = Boolean(group.is_enabled)
      this.groupToNotify = ![false, 0, '0', 'false'].includes(group.to_notify)
      this.groupsSelected = Array.isArray(group.groups) ? [...group.groups] : []
      this.mediaMvpVisibility = this.normalizeMediaMvpVisibility(group.media_mvp_visibility)
      this.groupTheme = group.theme || ''

      const sourceSurveys = Array.isArray(group.surveys) ? group.surveys : []

      this.surveys = sourceSurveys.map(survey => {
        const safeSurvey = survey || {}
        const optionsArray = Array.isArray(safeSurvey.options) ? safeSurvey.options : []
        return {
          uuid: safeSurvey.uuid || null,
          question: safeSurvey.question || '',
          multiple_answers: Boolean(safeSurvey.multiple_answers),
          isReanswerable: Boolean(safeSurvey.is_reanswerable === 1 || safeSurvey.is_reanswerable === true),
          isRequired: Boolean(safeSurvey.is_required === 1 || safeSurvey.is_required === true),
          options: optionsArray.map(option => {
            if (option && typeof option === 'object') {
              if (option.uuid) {
                return {
                  uuid: option.uuid,
                  option: option.option !== undefined ? option.option : option.value || ''
                }
              }
              return option.option !== undefined ? option.option : option.value || ''
            }
            return typeof option === 'string' ? option : ''
          }),
          noReplyAdded: optionsArray.some(option => isNoReplyOption(option)),
          allows_custom_answer: Boolean(safeSurvey.allows_custom_answer)
        }
      })

      if (!this.surveys.length) {
        this.surveys = [this.createNewSurvey()]
      }
    },
    addSurvey () {
      this.surveys.push(this.createNewSurvey())
    },
    hasBlankOption (survey) {
      return survey.options.some(option => {
        const value = typeof option === 'string' ? option : option.option
        return !value || value.trim() === ''
      })
    },
    removeSurvey (index) {
      if (this.surveys.length > 1) {
        this.surveys.splice(index, 1)
      }
    },
    addCustomAnswerOption (surveyIndex) {
      const survey = this.surveys[surveyIndex]
      // Remove the free-text and 'Ne souhaite pas répondre' options if they exist
      const normalOptions = survey.options.filter(option => !isLockedOption(option))
      const hasNoReply = survey.options.some(option => isNoReplyOption(option))
      // Add the free-text option at the penultimate position
      let newOptions = [...normalOptions]
      newOptions.push({ option: CUSTOM_ANSWER_OPTION })
      // Add 'Ne souhaite pas répondre' at the very end if it was present
      if (hasNoReply) newOptions.push({ option: NO_REPLY_OPTION })
      survey.options = newOptions
      survey.allows_custom_answer = true
    },
    removeOption (surveyIndex, optionIndex) {
      const survey = this.surveys[surveyIndex]
      const newOptions = survey.options.filter((_, i) => i !== optionIndex)
      if (newOptions.length === 1 && !isCustomAnswerOption(newOptions[0])) {
        this.errorMessage = 'Si le sondage ne comporte qu\'une seule réponse, celle-ci doit être personnalisée (Champ libre).'
        setTimeout(() => {
          this.errorMessage = ''
        }, 5000)
        return
      }
      this.errorMessage = ''
      const option = survey.options[optionIndex]
      if (isNoReplyOption(option)) {
        survey.noReplyAdded = false
      }
      if (isCustomAnswerOption(option)) {
        survey.allows_custom_answer = false
      }
      survey.options.splice(optionIndex, 1)
    },
    createNewSurvey () {
      return {
        question: '',
        options: [{ option: '' }, { option: '' }],
        multiple_answers: false,
        noReplyAdded: false,
        isReanswerable: false,
        isRequired: false,
        groups: [],
        allows_custom_answer: false
      }
    },
    addOption (surveyIndex) {
      let survey = this.surveys[surveyIndex]
      if (survey.options.some(option => typeof option === 'string' ? option.trim() === '' : option.option.trim() === '')) return
      const autreIndex = survey.options.findIndex(option => isCustomAnswerOption(option))
      const noReplyIndex = survey.options.findIndex(option => isNoReplyOption(option))
      // Insert before the free-text option if present, else before 'Ne souhaite pas répondre', else at end
      if (autreIndex > -1) {
        survey.options.splice(autreIndex, 0, { option: '' })
      } else if (noReplyIndex > -1) {
        survey.options.splice(noReplyIndex, 0, { option: '' })
      } else {
        survey.options.push({ option: '' })
      }
    },
    addNoReplyOption (surveyIndex) {
      this.removeNoReplyOption(surveyIndex)
      this.surveys[surveyIndex].options.push({ option: NO_REPLY_OPTION })
      this.surveys[surveyIndex].noReplyAdded = true
    },
    removeNoReplyOption (surveyIndex) {
      const index = this.surveys[surveyIndex].options.findIndex(option => isNoReplyOption(option))
      if (index > -1) {
        this.surveys[surveyIndex].options.splice(index, 1)
      }
      this.surveys[surveyIndex].noReplyAdded = false
    },
    async createGroup () {
      const isValid = await this.$refs.observer.validate()
      if (!isValid) {
        this.$buefy.dialog.alert({
          title: 'Erreurs dans le formulaire',
          message: 'Veuillez remplir correctement les champs du formulaire avant de continuer.'
        })
        return
      }
      let groupData = {
        uuid: this.editGroup ? this.editGroup.uuid : null,
        title: this.grouptitle,
        is_enabled: this.groupIsEnabled,
        to_notify: this.groupToNotify,
        domain: this.domain.uuid,
        groupUuids: this.groupsSelected,
        theme: this.groupTheme || null,
        surveys: this.surveys.map(survey => ({
          uuid: survey.uuid,
          question: survey.question,
          options: survey.options.map(option => {
            if (this.editGroup && option.uuid) {
              return {
                uuid: option.uuid,
                option: option.option
              }
            } else {
              return typeof option === 'string' ? option : option.option
            }
          }),
          multiple_answers: survey.multiple_answers,
          is_enabled: this.groupIsEnabled,
          is_reanswerable: survey.isReanswerable,
          // A single question has nothing to be required against.
          is_required: this.surveys.length > 1 && Boolean(survey.isRequired),
          allows_custom_answer: survey.allows_custom_answer
        }))
      }
      groupData.media_mvp_visibility = this.normalizeMediaMvpVisibility(this.mediaMvpVisibility)

      if (this.domain.uuid !== '2bc5decc-cd2e-46ad-92d7-f17234fdeebd') {
        groupData.start_at = this.groupStartAt
        groupData.end_at = this.groupEndAt

        groupData.surveys = groupData.surveys.map(survey => ({
          ...survey,
          start_at: this.groupStartAt,
          end_at: this.groupEndAt
        }))
      }
      if (this.editGroup) {
        const updateUrl = `/surveys/updategroup/${this.editGroup.uuid}`
        http.put(updateUrl, groupData)
          .then(({ data }) => {
            this.$emit('updated', data, {
              action: 'save',
              isEnabled: this.groupIsEnabled
            })
            this.$parent.close()
          }).catch(err => {
            console.log(err)
            this.$buefy.notification.open({
              duration: 3500,
              message: 'Nos serveurs rencontrent des difficultés à mettre à jour ce groupe de sondages',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          })
      } else {
        const createUrl = '/surveys/creategroup'
        http.post(createUrl, groupData).then(({ data }) => {
          this.$emit('created', data, {
            action: 'save',
            isEnabled: this.groupIsEnabled
          })
          this.$parent.close()
        }).catch(err => {
          console.log(err)
          this.$buefy.notification.open({
            duration: 3500,
            message: 'Nos serveurs rencontrent des difficultés à créer ce groupe de sondages',
            position: 'is-bottom-left',
            type: 'is-warning',
            hasIcon: true,
            icon: 'server'
          })
        })
      }
    },
    deleteGroup () {
      const isLegacySingle = Boolean(this.editGroup && this.editGroup.is_single)
      this.$buefy.dialog.confirm({
        title: 'Suppression',
        message: isLegacySingle
          ? 'Êtes-vous sûr de vouloir supprimer ce sondage ?'
          : 'Êtes-vous sûr de vouloir supprimer ce groupe de sondages ?',
        cancelText: 'Annuler',
        confirmText: 'Confirmer',
        type: 'is-danger',
        onConfirm: () => {
          const deleteUrl = isLegacySingle
            ? `/surveys/${this.editGroup.uuid}`
            : `/surveys/deletegroup/${this.editGroup.uuid}`
          http.delete(deleteUrl).then(() => {
            this.$emit('deleted')
          }).catch(err => {
            console.log(err)
            this.$buefy.notification.open({
              duration: 3500,
              message: 'Nos serveurs rencontrent des difficultés à supprimer ce groupe de sondages',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          }).finally(() => {
            this.$parent.close()
          })
        }
      })
    },
    duplicateSurvey () {
      if (!this.editGroup || !this.editGroup.uuid || this.duplicatingSurvey) return

      this.$dialog.confirm({
        title: 'Duplication du sondage',
        text: 'Voulez-vous vraiment dupliquer ce sondage ? En confirmant, une copie inactive sera créée dans les brouillons.',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        okButtonText: 'Dupliquer'
      }).then(async (result) => {
        if (result.isOk && result.hideReason === 'ok') {
          this.duplicatingSurvey = true
          try {
            const { data } = await http.post(`/surveys/${this.editGroup.uuid}/duplicate`)
            this.$buefy.notification.open({
              duration: 3000,
              message: 'Sondage dupliqué avec succès.',
              position: 'is-bottom-right',
              type: 'is-success'
            })
            this.$emit('updated', data, {
              action: 'duplicate',
              isEnabled: false
            })
            this.closeModal()
          } catch (error) {
            this.$buefy.notification.open({
              duration: 3500,
              message: 'Erreur lors de la duplication du sondage.',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          } finally {
            this.duplicatingSurvey = false
          }
        }
      })
    },
    notify () {
      const isLegacySingle = Boolean(this.editGroup && this.editGroup.is_single)
      const notifyUrl = `/surveys/${this.editGroup.uuid}/${isLegacySingle ? 'notify' : 'notify-group'}`
      http.post(notifyUrl).then(res => {
        this.$buefy.notification.open({
          duration: 3000,
          message: isLegacySingle
            ? 'Le sondage va être notifié !'
            : 'Le sondage multiple va être notifié !',
          position: 'is-bottom-left',
          type: 'is-success',
          hasIcon: true
        })
        this.$emit('updated', {
          group: {
            ...this.editGroup,
            is_notified: true
          }
        }, {
          action: 'notify'
        })
        this.$parent.close()
      }).catch(err => {
        console.log(err)
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Nos serveurs rencontrent des difficultés à mettre à jour ce groupe de sondage',
          position: 'is-bottom-left',
          type: 'is-warning',
          icon: 'server'
        })
      })
    },
    getDuplicateOptionIndexes (options) {
      const seen = {}
      const duplicates = []
      options.forEach((opt, idx) => {
        const val = (typeof opt === 'string' ? opt : opt.option).trim().toLowerCase()
        if (val && seen[val] !== undefined) {
          duplicates.push(idx)
          if (!duplicates.includes(seen[val])) {
            duplicates.push(seen[val])
          }
        } else if (val) {
          seen[val] = idx
        }
      })
      return duplicates
    }
  },
  watch: {
    domain: {
      handler (val) {
        if (val && val.uuid === '2bc5decc-cd2e-46ad-92d7-f17234fdeebd') {
          this.groupStartAt = null
          this.groupEndAt = null
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-editor-modal {
  max-width: 1040px;
}

.survey-editor-content {
  display: flex;
  flex-direction: column;
}

.survey-question {
  padding: 1rem 0 2rem;
}

.survey-question + .survey-question {
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.survey-question-layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(280px, 2fr);
  gap: 2.5rem;
  align-items: start;
}

.survey-question-main {
  min-width: 0;
  padding: 0.25rem 0.5rem;
}

.survey-question-title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
}

.survey-question-title-provider {
  flex: 1 1 auto;
  min-width: 0;
}

.survey-question-input,
.survey-group-title-input {
  display: block;
  width: 100%;
  height: 3.25rem;
  padding: 0 1rem;
  color: #6b7280;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  outline: none;
}

.survey-question-input::placeholder,
.survey-group-title-input::placeholder,
.survey-option-input::placeholder {
  color: #c7ccd3;
  font-size: 1rem;
}

.survey-question-input:focus,
.survey-group-title-input:focus {
  border-color: #445877;
  box-shadow: 0 0 0 3px rgba(68, 88, 119, 0.14);
}

.survey-question-input--error,
.survey-question-input--error:focus,
.survey-group-title-input--error,
.survey-group-title-input--error:focus {
  border-color: #ef4444;
}

.survey-question-remove,
.survey-option-remove {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  background: transparent;
  border: 0;
  outline: none;
}

.survey-question-remove {
  width: 2rem;
  height: 3.25rem;
}

.survey-question-remove:hover,
.survey-option-remove:hover {
  color: #991b1b;
}

.survey-question-settings {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-height: 6.6rem;
  padding: 1.5rem;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.25rem;
}

.survey-question-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #374151;
  font-size: 0.9rem;
  line-height: 1.3;
}

.survey-question-setting span {
  cursor: pointer;
}

.survey-options-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  margin-top: 1.35rem;
  padding-left: 1.75rem;
}

.survey-option-row {
  display: grid;
  grid-template-columns: 1.15rem minmax(0, 1fr) 2rem;
  align-items: center;
  min-height: 2.5rem;
}

.survey-option-indicator {
  display: block;
  width: 0.45rem;
  height: 0.45rem;
  margin-left: 0.2rem;
  background: #6b7280;
  border-radius: 999px;
}

.survey-option-field {
  position: relative;
  min-width: 0;
}

.survey-option-input {
  display: block;
  width: 100%;
  min-height: 2.25rem;
  padding: 0.35rem 0.5rem;
  color: #374151;
  background: transparent;
  border: 0;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 0;
  font-size: 0.95rem;
  outline: none;
}

.survey-option-input:focus {
  border-bottom-color: #445877;
}

.survey-option-input--error {
  color: #b91c1c;
  border-bottom-color: #ef4444;
}

.survey-option-input--locked {
  cursor: not-allowed;
  color: #6b7280;
}

.survey-option-error {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  color: #b91c1c;
  font-size: 0.75rem;
  transform: translateY(-50%);
  pointer-events: none;
}

.survey-option-remove {
  width: 2rem;
  height: 2.25rem;
  font-size: 0.8rem;
}

.survey-option-shortcuts {
  display: grid;
  grid-template-columns: 1.15rem repeat(7, auto);
  align-items: center;
  justify-content: start;
  column-gap: 0.3rem;
  row-gap: 0.25rem;
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.survey-option-shortcuts a {
  color: #3465d9;
  text-decoration: underline;
}

.survey-option-link-wrapper--disabled {
  cursor: not-allowed;
}

.survey-option-link--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.survey-general-settings {
  margin-top: 1rem;
  padding: 1.5rem;
  border: 1px solid #eef0f3;
}

.survey-group-title-input {
  margin-bottom: 1.25rem;
}

.survey-general-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  margin-top: 1.25rem;
}

.survey-general-field {
  min-width: 0;
}

.survey-general-label {
  margin-bottom: 0.65rem;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.survey-general-field ::v-deep input:not([type="radio"]):not([type="checkbox"]),
.survey-general-field ::v-deep .select,
.survey-general-field ::v-deep .select select {
  width: 100%;
}

.survey-theme-select {
  width: 100%;
  height: 2.5rem;
  padding: 0 0.75rem;
  color: #374151;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
  outline: none;
}

.survey-theme-select:focus {
  border-color: #445877;
  box-shadow: 0 0 0 3px rgba(68, 88, 119, 0.14);
}

.survey-theme-select--error,
.survey-theme-select--error:focus {
  border-color: #ef4444;
}

.survey-media-visibility {
  margin-top: 1.25rem;
}

.survey-media-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
}

.survey-media-options label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #374151;
  font-size: 0.9rem;
}

.survey-media-options input[type="radio"] {
  flex: 0 0 auto;
  width: 1rem;
  height: 1rem;
  margin: 0;
}

.survey-editor-actions,
.survey-editor-actions__left,
.survey-editor-actions__right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
}

.survey-editor-actions {
  justify-content: space-between;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.survey-field-error {
  margin: 0.25rem 0 0;
  color: #dc2626;
  font-size: 0.75rem;
}

.survey-editor-error {
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
}

@media (max-width: 860px) {
  .survey-question-layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.25rem;
  }

  .survey-question-settings {
    min-height: 0;
  }

  .survey-general-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .survey-editor-actions {
    align-items: stretch;
  }

  .survey-editor-actions__left,
  .survey-editor-actions__right {
    width: 100%;
  }

  .survey-editor-actions__right {
    justify-content: flex-end;
  }
}

@media (max-width: 560px) {
  .survey-question-main {
    padding-right: 0;
    padding-left: 0;
  }

  .survey-options-list {
    padding-left: 0;
  }

  .survey-option-shortcuts {
    display: flex;
    flex-wrap: wrap;
  }

  .survey-general-settings {
    padding: 1rem;
  }
}
</style>

<style>
/* Used for save button tooltip formatting */
.v-popper__inner {
  white-space: pre-line;
}
</style>
